/**
 * 回忆页“最近一趟旅行”这一块（TRV-06；数据与规矩见 ./travelMemory）。整张卡是一个链接，去攻略页的那份计划（/guides/plan/:planId）：
 * 那里有整页回忆手账、资料来源和“重画这张手账”（重画只在那里、由主人自己点，这里不给按钮）。
 * - 去过和没去成分开：每个地点前一枚章——“到过”是实心的印（只来自真实到访事件），“下次”是虚线框；顺路建议另标“顺路”。
 * - 计划和实况分开：卡片标明这是“回忆手账”，标题下一句写这趟实际怎样（去了几个地方、几个留到下次），不把计划里写的当成到过。
 * - 图：画好了放一张小图（加载不出来就收起，说一句“手账图没加载出来”）；没画成、结果未确认、还在画、没有配图时不放手账图，
 *   图位放一本合着的手账（V-18：攻略手账页同一本封面 ASSET-UI-028，纯装饰），说一句图的情况——
 *   没画成、没有配图、没接插画都说“这一页是 TA 用文字记的”，不把没画成摆在第一屏（重画在攻略页，那里照实说）；文字（标题、实况、地点和章）都照常可读。
 * - 手账按设计不画图（本地心愿，JournalView.state = text_only，DS5）：不留图位，也不说“这一页只有文字”；标题、实况、地点和章、时间照常。
 * - 演示数据挂“演示”标记。
 */
import { useId, useState } from "react";
import { Link } from "react-router";
import { DataOriginBadge, Icon, PlanetEventLine } from "@/shared/ui";
import type { JournalView, PlanView } from "@/features/journey/travelPlan/model";
import { useLatestTripMemory } from "./travelMemory";

export function TripMemory() {
  const read = useLatestTripMemory();
  if (read.state !== "ready") return null;
  // 换了一份计划（切宠物、心愿换了）就换一张卡：图片加载失败的记号不带到另一份上。
  return <TripMemoryCard key={read.view.key} view={read.view} />;
}

/** 图的那一句（不放图时）：说清图怎样，文字都在。不说“失败 / 重试”，重画在攻略页。 */
export function artNote(journal: JournalView, broken: boolean): string {
  if (broken) return "手账图没加载出来，文字都在这里";
  switch (journal.state) {
    case "failed":
      return "这一页是 TA 用文字记的";
    case "unknown":
      return "手账图还没确认画好，文字都在这里";
    case "drawing":
      return "手账图还在画，文字先看这里";
    case "refused":
    case "none":
      return "这一页是 TA 用文字记的";
    default:
      return "文字都在这里";
  }
}

export function TripMemoryCard({ view }: { view: PlanView }) {
  const id = useId();
  const [broken, setBroken] = useState(false);
  const { journal } = view;
  const text = journal.text;
  const image = journal.imageUrl && !broken ? journal.imageUrl : null;
  const textOnly = journal.state === "text_only";
  return (
    <section className="ps-mem-trip" aria-labelledby={`${id}-title`} data-trip-plan={view.key}>
      <div className="ps-mem-trip__head">
        <h2 id={`${id}-title`}>最近一趟旅行</h2>
        {view.isDemo ? <DataOriginBadge origin="fixture" label="演示" /> : null}
      </div>
      <Link to={view.href} className="ps-mem-trip__card" data-journal={broken ? "broken" : journal.state}>
        {textOnly ? null : (
          <span className="ps-mem-trip__art">
            {image ? (
              <img src={image} alt={`${journal.heading}的画面`} loading="lazy" onError={() => setBroken(true)} />
            ) : (
              <span className="ps-mem-trip__blank ps-mem-trip__cover" aria-hidden="true" />
            )}
          </span>
        )}
        <span className="ps-mem-trip__body">
          <span className="ps-mem-trip__kicker">{journal.heading}</span>
          <strong className="ps-mem-trip__title">{text?.title ?? view.title}</strong>
          <span className="ps-mem-trip__line">{view.summary}</span>
          <PlanetEventLine event={journal.planetEvent} />
          {text?.stations.length ? (
            <span className="ps-mem-trip__places">
              {text.stations.map((station) => (
                <span key={station.key} className="ps-mem-trip__place" data-stamp={station.stamp?.kind ?? "none"}>
                  {station.stamp ? <span className="ps-mem-trip__stamp">{station.stamp.label}</span> : null}
                  <span className="ps-mem-trip__name">{station.name}</span>
                  {station.role === "suggested" ? <span className="ps-mem-trip__role">顺路</span> : null}
                </span>
              ))}
            </span>
          ) : null}
          {image || textOnly ? null : <span className="ps-mem-trip__note">{artNote(journal, broken)}</span>}
          {journal.identity && !broken ? <span className="ps-mem-trip__note">{journal.identity}</span> : null}
          {journal.time ? <span className="ps-mem-trip__time">{journal.time}</span> : null}
          <span className="ps-mem-trip__open">
            看这页回忆手账 <Icon name="chevron" size={14} />
          </span>
        </span>
      </Link>
    </section>
  );
}
